import { Plus, Search, X } from "lucide-react";
import {
	Button,
	Input,
	Modal,
	Notification,
	Select,
	Table,
	TextArea,
	useNotification,
} from "../../components/FormComponents";
import Pagination from "../../components/Pagination";
import { ICON_OPTIONS } from "../../constants/themes";
import { useCrudPage } from "../../hooks/useCrudPage";

export default function Modules() {
	const { notification, showNotification } = useNotification();

	const {
		data: modules,
		loading,
		saving,
		page,
		limit,
		total,
		search,
		isModalOpen,
		editingId,
		formData,
		errors,
		setPage,
		setSearch,
		updateField,
		handleOpenModal: crudOpen,
		handleCloseModal,
		handleSubmit,
		handleDelete,
		setFormData,
	} = useCrudPage("/modules", null, {
		onSuccess: (action) => {
			const messages = {
				created: "Módulo criado com sucesso!",
				updated: "Módulo atualizado com sucesso!",
				deleted: "Módulo deletado com sucesso!",
			};
			showNotification(messages[action]);
		},
		defaultFormData: {
			name: "",
			value: "",
			description: "",
			field_type: "text",
			is_global: true,
			icon: "box",
			subtitle: "",
			image_url: "",
			features: [],
		},
	});

	const handleOpenModal = (moduleData = null) => {
		if (moduleData) {
			const features =
				typeof moduleData.features === "string"
					? JSON.parse(moduleData.features)
					: moduleData.features || [];
			crudOpen({ ...moduleData, features });
		} else {
			crudOpen();
		}
	};

	const columns = [
		{ key: "name", label: "Nome" },
		{
			key: "value",
			label: "Valor",
			render: (val) => (
				<span className="text-green-400 font-bold">
					R$ {parseFloat(val).toFixed(2)}
				</span>
			),
		},
		{ key: "field_type", label: "Tipo de Campo" },
		{
			key: "is_global",
			label: "Global",
			render: (val) => (
				<span className={val ? "text-green-400" : "text-slate-400"}>
					{val ? "✓ Sim" : "✗ Não"}
				</span>
			),
		},
	];

	return (
		<div className="space-y-6">
			<Notification notification={notification} />

			<div className="flex items-center justify-between">
				<h1 className="text-2xl font-bold text-white">Gerenciar Módulos</h1>
				<Button type="button" onClick={() => handleOpenModal()}>
					<Plus className="w-4 h-4 inline mr-2" />
					Novo Módulo
				</Button>
			</div>

			{/* Search */}
			<div className="relative">
				<Search
					className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400"
					aria-hidden="true"
				/>
				<input
					type="text"
					value={search}
					onChange={(e) => {
						setSearch(e.target.value);
						setPage(1);
					}}
					placeholder="Buscar modulos..."
					aria-label="Buscar módulos"
					className="w-full pl-10 pr-4 py-2.5 bg-slate-900/50 border border-white/10 rounded-lg text-slate-300 placeholder-slate-500 focus:outline-none focus:border-cyan-500/50 focus:ring-1 focus:ring-cyan-500/30 transition-all"
				/>
			</div>

			<div className="bg-slate-900/50 backdrop-blur border border-white/5 rounded-xl p-6">
				<Table
					columns={columns}
					data={modules}
					loading={loading}
					onEdit={handleOpenModal}
					onDelete={(id) =>
						handleDelete(id, "Tem certeza que deseja deletar este módulo?")
					}
				/>

				<Pagination
					page={page}
					limit={limit}
					total={total}
					onPageChange={setPage}
				/>
			</div>

			<Modal
				isOpen={isModalOpen}
				title={editingId ? "Editar Módulo" : "Novo Módulo"}
				onClose={handleCloseModal}
				onSubmit={handleSubmit}
				submitLabel={editingId ? "Atualizar" : "Criar"}
				loading={saving}
			>
				<div className="space-y-4">
					<Input
						label="Nome *"
						value={formData.name || ""}
						onChange={(e) => updateField("name", e.target.value)}
						placeholder="Ex: Suporte Premium"
						error={errors.name}
					/>
					<Input
						label="Valor *"
						type="number"
						step="0.01"
						value={formData.value || ""}
						onChange={(e) => updateField("value", e.target.value)}
						placeholder="500.00"
						error={errors.value}
					/>
					<TextArea
						label="Descricao"
						value={formData.description || ""}
						onChange={(e) => updateField("description", e.target.value)}
						placeholder={
							"Descricao do modulo para exibir na proposta.\nUse linhas com - para listar features:\n- Relatorios de conversao\n- Graficos de performance\n- Exportacao de dados"
						}
						rows="5"
					/>
					<Select
						label="Tipo de Campo"
						value={formData.field_type || "checkbox"}
						onChange={(e) => updateField("field_type", e.target.value)}
						options={[
							{ value: "checkbox", label: "Checkbox" },
							{ value: "text", label: "Texto" },
							{ value: "select", label: "Seleção" },
							{ value: "number", label: "Número" },
						]}
					/>
					<div className="flex items-center gap-3 p-3 bg-slate-800/50 rounded border border-slate-700">
						<input
							id="is_global_checkbox"
							type="checkbox"
							checked={formData.is_global ?? true}
							onChange={(e) => updateField("is_global", e.target.checked)}
							className="w-4 h-4 cursor-pointer"
						/>
						<label
							htmlFor="is_global_checkbox"
							className="text-sm text-slate-300 cursor-pointer flex-1"
						>
							Disponível para todos os clientes (Global)
						</label>
					</div>

					{/* Visual Fields */}
					<div className="border-t border-slate-700 pt-4 mt-2">
						<p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">
							Campos Visuais (Proposta)
						</p>
						<div className="grid grid-cols-2 gap-3">
							<Select
								label="Icone"
								value={formData.icon || "box"}
								onChange={(e) => updateField("icon", e.target.value)}
								options={ICON_OPTIONS.filter((o) => o.value)}
							/>
							<Input
								label="Subtitulo"
								value={formData.subtitle || ""}
								onChange={(e) => updateField("subtitle", e.target.value)}
								placeholder="Ex: Modulo essencial"
							/>
						</div>
						<Input
							label="URL da Imagem (opcional)"
							value={formData.image_url || ""}
							onChange={(e) => updateField("image_url", e.target.value)}
							placeholder="https://..."
						/>
						{formData.image_url && (
							<div className="mt-1 rounded border border-white/5 overflow-hidden bg-slate-800/50 max-h-24">
								<img
									src={formData.image_url}
									alt="Preview"
									className="w-full h-full object-cover"
								/>
							</div>
						)}
					</div>

					{/* Features Builder */}
					<div className="border-t border-slate-700 pt-4 mt-2">
						<div className="flex items-center justify-between mb-2">
							<p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
								Features Estruturadas ({(formData.features || []).length})
							</p>
							<button
								type="button"
								onClick={() =>
									setFormData({
										...formData,
										features: [
											...(formData.features || []),
											{ icon: "star", title: "", description: "" },
										],
									})
								}
								className="text-xs text-cyan-400 hover:text-cyan-300 flex items-center gap-1"
							>
								<Plus className="w-3 h-3" aria-hidden="true" /> Adicionar
							</button>
						</div>
						<p className="text-[10px] text-slate-600 mb-2">
							Features com icone e titulo aparecem na proposta em vez dos
							bullets de texto.
						</p>
						{(formData.features || []).map((feat, fi) => (
							<div
								key={feat.id || `feature-${fi}`}
								className="p-2 bg-slate-900/50 rounded border border-slate-700/30 mb-2"
							>
								<div className="flex items-center justify-between mb-1">
									<span className="text-[10px] text-slate-500">
										Feature {fi + 1}
									</span>
									<button
										type="button"
										onClick={() =>
											setFormData({
												...formData,
												features: formData.features.filter((_, i) => i !== fi),
											})
										}
										className="text-slate-500 hover:text-red-400 p-1"
										aria-label="Remover feature"
									>
										<X className="w-3 h-3" />
									</button>
								</div>
								<div className="grid grid-cols-[80px_1fr] gap-2">
									<Select
										label="Icone"
										value={feat.icon || ""}
										onChange={(e) => {
											const updated = [...formData.features];
											updated[fi] = { ...updated[fi], icon: e.target.value };
											setFormData({ ...formData, features: updated });
										}}
										options={ICON_OPTIONS.filter((o) => o.value)}
									/>
									<Input
										label="Titulo"
										value={feat.title || ""}
										onChange={(e) => {
											const updated = [...formData.features];
											updated[fi] = { ...updated[fi], title: e.target.value };
											setFormData({ ...formData, features: updated });
										}}
										placeholder="Titulo da feature"
									/>
								</div>
								<Input
									label="Descricao"
									value={feat.description || ""}
									onChange={(e) => {
										const updated = [...formData.features];
										updated[fi] = {
											...updated[fi],
											description: e.target.value,
										};
										setFormData({ ...formData, features: updated });
									}}
									placeholder="Descricao curta..."
								/>
							</div>
						))}
					</div>
				</div>
			</Modal>
		</div>
	);
}
